import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Type,
  ViewChild,
  ChangeDetectionStrategy,
  ElementRef,
} from '@angular/core';
import { MatSortModule, Sort } from '@angular/material/sort';
import { IQueryParam } from 'modelos/src';

import { MatIconModule } from '@angular/material/icon';

import { ColumnSelectComponent } from './column-select/column-select.component';
import { PaginatorComponent } from './paginator/paginator.component';
import {
  FiltroComponent,
  IFiltroTabla,
  IRegExpSearch,
} from './filtro/filtro.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RouterModule } from '@angular/router';
import { DynamicPipe } from '../pipes/dynamic';

export interface ICrearBoton {
  mostrar: boolean;
  tooltip?: string;
  icon?: string;
  accion: () => void;
  color?: string;
}

export interface IExportarBoton {
  mostrar: boolean;
  tooltip?: string;
  icon?: string;
  accion: () => void;
  color?: string;
  loading?: boolean;
}

export interface IImportarBoton {
  mostrar: boolean;
  tooltip?: string;
  icon?: string;
  accion: () => void;
  color?: string;
  loading?: boolean;
}

export interface IAcciones<T> {
  icon: string;
  tooltip: string;
  color?: string;
  click: (dato: T) => void;
  disabled?: (dato: T) => boolean;
  oculta?: (dato: T) => boolean;
}

export interface IRowIcon {
  tipo?: 'material' | 'img';
  icon: string;
  tooltip?: string;
  color?: string;
}

export interface IRowButton<T> {
  // tipo?: 'material' | 'img';
  text?: string;
  tooltip?: string;
  color?: string;
  class?: string;
  click?: (dato: T) => any;
}

export interface IRowLink {
  link: string;
  tooltip?: string;
  class?: string;
  route?: string[] | string;
  click?: () => void;
}

interface IPipe {
  pipe: Type<any>;
  args?: any;
}

/**
 * Columna de selección múltiple.
 *
 * La tabla no guarda la selección: la mantiene el componente que la usa, y acá
 * solo se declara cómo leerla y cómo modificarla. Es lo que permite que la
 * selección sobreviva a un cambio de página o de filtro (la tabla se rearma en
 * cada consulta) y que el dueño de la pantalla decida el tope y el alcance.
 */
export interface ICheckboxColumna<T> {
  checked: (dato: T) => boolean;
  change: (dato: T, checked: boolean) => void;
  disabled?: (dato: T) => boolean;
  /**
   * Checkbox del encabezado: selecciona o limpia lo que está en pantalla.
   * `indeterminate` marca la página parcialmente seleccionada.
   */
  header?: {
    checked: () => boolean;
    indeterminate: () => boolean;
    change: (checked: boolean) => void;
  };
}

export type IColumnas<T> = {
  header: {
    label: string;
    sort?: string;
    sortable?: boolean;
    hidden?: boolean;
    maxWidth?: string; // Nuevo: ancho máximo de la columna (ej: '300px', '20%')
    // La columna no se puede ocultar desde el selector de columnas. Para las
    // que son parte del mecanismo de la tabla y no un dato (la de selección):
    // ocultarlas dejaría la pantalla sin forma de seleccionar.
    fija?: boolean;
  };
  row: {
    field?: keyof T | 'acciones';
    checkbox?: ICheckboxColumna<T>;
    parse?: (dato: T) => string | number | undefined;
    icono?: (dato: T) => IRowIcon;
    boton?: (dato: T) => IRowButton<T> | undefined;
    html?: (dato: T) => string | null;
    suffix?: string;
    prefix?: string;
    pipe?: IPipe;
    acciones?: IAcciones<T>[];
    accionesGrouped?: IAcciones<T>[];
    noData?: string;
    claseCss?: (dato: T) => string | undefined;
    links?: (dato: T) => IRowLink[] | undefined;
    tooltip?: (dato: T) => string | undefined;
  };
  footer?: {
    fn: (dato: IColumnas<T>) => string | number | undefined;
    pipe?: IPipe;
    suffix?: string;
    prefix?: string;
  };
};

@Component({
  imports: [
    RouterModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatCheckboxModule,
    DynamicPipe,
    MatMenuModule,
    ColumnSelectComponent,
    PaginatorComponent,
    FiltroComponent,
  ],
  selector: 'app-tabla',
  templateUrl: './tabla.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tabla.component.scss'],
})
export class TablaComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('topPaginator') top?: PaginatorComponent;
  @ViewChild('botPaginator') bottom?: PaginatorComponent;

  private sortValueInicial?: string = '_id';

  // If para lso paginators
  @Input() paginatorArriba = false;
  @Input() paginatorAbajo = true;
  @Input() selectorColumnas = true;

  // Crear-Exportar
  @Input() public crear?: ICrearBoton;
  @Input() public exportar?: IExportarBoton;
  @Input() public importar?: IImportarBoton;
  // Datos de la tabla
  @Input() public totalCount: number = 0;
  @Input() public datos?: any[] = [];
  public dataSource?: MatTableDataSource<any>;

  // Nombre para guardar las columnas
  @Input() public name?: string;
  @Input() public columnas?: IColumnas<any>[];
  /** Texto propio para el caso "no hay nada cargado" (cero real, sin filtros). */
  @Input() public mensajeVacio?: string;
  public columnsDef: string[] = [];

  // Filtros
  @Input() filtros: IFiltroTabla[] = [];
  @Input() search?: IRegExpSearch;

  // Paginacion
  @Input() pageSizeOptions: number[] = [];
  @Input() pageSize: number = 10;
  /** Paginador minimal: tamaño fijo 5, sin selector de tamaño, sin
   * botones primero/último ni "Ir a", solo anterior/siguiente + rango. */
  @Input() paginatorSimple = false;

  /** Muestra un spinner sobre la tabla mientras se cargan los datos. */
  @Input() loading = false;

  /**
   * false hasta la primera vez que llegan datos (o si ya venían al init).
   * Evita mostrar "no hay datos" durante la carga inicial: mientras es false
   * se muestra el esqueleto. Un timeout de seguridad lo libera por si una tabla
   * estática nunca emite datos.
   */
  public datosCargados = false;

  /**
   * Esqueleto de la carga inicial: ocupa lo que queda de vista debajo del encabezado, sin el
   * paginador, y nada más; al llegar, los datos la achican o la agrandan. Mínimo 3 filas (una
   * tabla que arranca abajo de la vista) y, en las tablas chicas, máximo las 5 de su página.
   * El alto va directo al estilo: un binding cambiaría en el mismo ciclo en que se mide.
   */
  @ViewChild('esqueleto') set esqueleto(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    if (!ref) return;
    const el = ref.nativeElement;
    // Al cuadro siguiente: acá la ruta todavía no está en su lugar (top 0, paginador sin alto).
    requestAnimationFrame(() => {
      const paginador =
        el.parentElement?.parentElement?.querySelector<HTMLElement>(
          '.paginator-container',
        )?.offsetHeight ?? 0;
      // 32: el margen hasta el paginador y el de abajo, el mismo que deja la barra lateral.
      const libre =
        window.innerHeight - el.getBoundingClientRect().top - paginador - 32;
      const fila = 52;
      const maximo = this.paginatorSimple ? 5 * fila + 16 : Infinity;
      el.style.height = `${Math.min(Math.max(libre, 3 * fila + 16), maximo)}px`;
    });
  }

  /**
   * true mientras se re-consultan datos con la tabla ya cargada (cambio de
   * filtro, búsqueda, orden o página). Muestra una progress bar en el header
   * en vez del esqueleto. Se resetea al llegar los datos nuevos.
   */
  public recargando = false;

  // Query
  @Input() public query: IQueryParam = {};
  @Output() public queryChange = new EventEmitter<IQueryParam>();

  constructor(private dom: DomSanitizer) {}

  /** true si hay algún filtro de tabla o búsqueda con valor aplicado. */
  get hayFiltroActivo(): boolean {
    if (this.search?.value) return true;
    return (this.filtros || []).some((f) => {
      const v = f.filter?.value;
      const tieneValor = Array.isArray(v)
        ? v.length > 0
        : v !== undefined && v !== null && v !== '';
      return tieneValor || !!f.desde || !!f.hasta;
    });
  }

  /**
   * Mensaje a mostrar cuando la tabla no tiene datos. Son dos casos distintos y
   * el usuario tiene que poder distinguirlos: que el filtro no haya traído nada
   * (hay algo cargado, la vista lo está escondiendo) o que no haya nada cargado
   * en el ámbito. Se habla de "ámbito" y no de "sistema" porque la unidad de
   * negocio / división seleccionada también acota la consulta, y no viaja en
   * `filtros` sino dentro de `query.filter`.
   */
  get mensajeSinDatos(): string {
    if (this.hayFiltroActivo) {
      return 'No hay elementos que coincidan con el filtro aplicado';
    }
    return this.mensajeVacio || 'Todavía no hay nada cargado en este ámbito';
  }

  /** `search_off` cuando el vacío lo produjo el filtro; `inbox` cuando no hay nada. */
  get iconoSinDatos(): string {
    return this.hayFiltroActivo ? 'search_off' : 'inbox';
  }

  /** Qué puede hacer el usuario con este vacío. */
  get sugerenciaSinDatos(): string | undefined {
    if (this.hayFiltroActivo) {
      return 'Probá cambiando o limpiando los filtros';
    }
    return this.crear?.mostrar
      ? 'Usá el botón + para cargar el primero'
      : undefined;
  }

  trackBy(index: number, list: any): string {
    // Preferir _id: estable entre re-emisiones (WS) => MatTable reutiliza el
    // DOM de las filas en lugar de reconstruir todo. Fallback a JSON.stringify
    // para filas sin _id (agrupaciones, etc).
    return list?._id ?? JSON.stringify(list);
  }

  public async sortChange(event: Sort): Promise<void> {
    const sort = event.active;
    if (event.direction === 'asc') {
      this.query.sort = sort;
    } else if (event.direction === 'desc') {
      const campos = sort.split(' ');
      campos.forEach((campo, index) => {
        campos[index] = `-${campo}`;
      });
      this.query.sort = campos.join(' ');
    } else {
      this.query.sort = this.sortValueInicial;
    }

    this.saveSort(this.query.sort || '');
    this.marcarRecarga();
    this.queryChange.emit(this.query);
  }

  /** Marca recarga (progress bar) solo si la tabla ya tenía datos. */
  private marcarRecarga(): void {
    if (!this.datosCargados) return;
    this.recargando = true;
    // Red de seguridad: la barra se apaga cuando llegan datos nuevos, y eso depende
    // de que cambie la referencia de `datos`. Si algún camino reemite lo mismo, sin
    // esto la barra queda prendida para siempre. Mismo criterio que el guard de
    // `datosCargados`.
    clearTimeout(this.timerRecarga);
    this.timerRecarga = setTimeout(() => (this.recargando = false), 10000);
  }

  private timerRecarga?: ReturnType<typeof setTimeout>;

  private finRecarga(): void {
    this.recargando = false;
    clearTimeout(this.timerRecarga);
  }

  private saveSort(sort: string): void {
    if (this.name) {
      localStorage.setItem(`sort-${this.name}`, sort);
    }
  }

  private getSavedSort(): string | undefined {
    if (this.name) {
      const saved = localStorage.getItem(`sort-${this.name}`);
      if (saved) {
        return saved;
      }
    }
    return undefined;
  }

  public columnsChange(event: string[]): void {
    window.requestAnimationFrame(() => (this.columnsDef = event));
  }

  public filterChange() {
    this.query.page = 0;
    this.marcarRecarga();
    this.queryChange.emit(this.query);
  }

  public paginationChange() {
    this.syncPaginators();
    this.marcarRecarga();
    this.queryChange.emit(this.query);
  }

  public syncPaginators() {
    const length = this.totalCount;
    const pageIndex = +(this.query.page || 0);
    const pageSize = +(this.query.limit || 10);
    if (this.bottom) {
      this.bottom.length = length;
      this.bottom.pageIndex = pageIndex;
      this.bottom.pageSize = pageSize;
    }
    if (this.top) {
      this.top.length = length;
      this.top.pageIndex = pageIndex;
      this.top.pageSize = pageSize;
    }
  }

  private setDataSource() {
    this.dataSource = new MatTableDataSource(this.datos);
  }

  // Deriva columnsDef directamente de `columnas` (mismo criterio que
  // column-select). Se usa para el render inicial sincrónico: sin esto,
  // columnsDef solo se llena vía el @Output de column-select (emitido dentro
  // de requestAnimationFrame); si el hilo está ocupado, el rAF se posterga y
  // la tabla queda sin columnas (vacía) varios segundos. column-select luego
  // reemite aplicando orden/ocultas guardadas.
  private computeColumnsDef(): string[] {
    return (this.columnas || [])
      .filter((col) => !col.header.hidden)
      .map(
        (col) =>
          (col.header.sort || col.row.field || col.header.label) as string,
      );
  }

  /**
   * Cache string → SafeHtml. `bypassSecurityTrustHtml` devuelve un objeto nuevo
   * en cada llamada, así que sin cache el binding `[innerHTML]` cambiaba en cada
   * change detection (el reloj de navigation dispara una por segundo) y Angular
   * reescribía el DOM de la celda: se perdía la selección de texto al instante.
   * Se vacía al llegar datos nuevos para no crecer sin límite.
   */
  private htmlCache = new Map<string, SafeHtml>();

  public html(value?: string | null) {
    if (!value) {
      return;
    }
    let safe = this.htmlCache.get(value);
    if (!safe) {
      safe = this.dom.bypassSecurityTrustHtml(value);
      this.htmlCache.set(value, safe);
    }
    return safe;
  }

  /**
   * ¿Alguna de las acciones agrupadas es visible para esta fila?
   *
   * El botón `more_vert` se renderizaba siempre, incluso cuando todas las acciones
   * del menú estaban ocultas por permisos o por estado: abría un menú vacío.
   */
  public hayAccionesVisibles(acciones: IAcciones<any>[], row: any): boolean {
    return acciones.some((a) => !a?.oculta?.(row));
  }

  private sortInicial() {
    const sort = this.getSavedSort();
    if (sort) {
      this.query.sort = sort;
      this.queryChange.emit(this.query);
    }
  }

  ngOnInit() {
    this.sortValueInicial = this.query.sort || '';
    this.setDataSource();
    // Render inicial inmediato de columnas (no esperar el rAF de column-select).
    this.columnsDef = this.computeColumnsDef();
    this.sortInicial();
    // Si al init ya hay datos (tabla con datos estáticos), marcar como cargada.
    if (this.datos?.length) {
      this.datosCargados = true;
    }
    // Red de seguridad: si nunca llega una emisión de datos, liberar el estado
    // de carga para no dejar el esqueleto infinito.
    setTimeout(() => (this.datosCargados = true), 8000);
  }

  ngOnChanges(changes: any) {
    if (changes.datos) {
      // La primera emisión real de datos (no el binding inicial) marca la carga
      // como completa. A partir de acá, si no hay filas se muestra el mensaje.
      if (!changes.datos.firstChange) {
        this.datosCargados = true;
      }
      // Llegaron datos nuevos: termina la recarga.
      this.finRecarga();
      this.htmlCache.clear();
      this.setDataSource();
    }
    if (changes.columnas && !changes.columnas.firstChange) {
      // Limpiar columnsDef para evitar que MatTable intente renderizar columnas que ya
      // no están registradas como matColumnDef mientras *ngFor reconstruye las columnas.
      // column-select emitirá el nuevo columnsDef en el siguiente frame.
      this.columnsDef = [];
    }
  }

  ngOnDestroy() {
    clearTimeout(this.timerRecarga);
  }
}
